import { useMemo } from 'react';

import { Flex } from '@chakra-ui/react';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@mesh.ai/rabbit';
import { getHours, getMinutes, setHours, setMinutes, isValid } from 'date-fns';

const HOURS = Array.from({ length: 12 }, (_, i) => (i + 1).toString());
const MINUTES = Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, '0'));

type TimePickerProps = {
  time: Date;
  onTimeUpdate: (newTime: Date) => void;
  disabled?: boolean;
};

type TimeComponent = 'hour' | 'minute' | 'amPm';

const convertTo24Hour = (hour: number, amPm: string): number => {
  let hourIn24 = hour;
  if (amPm === 'PM' && hour < 12) hourIn24 += 12;
  if (amPm === 'AM' && hour === 12) hourIn24 = 0;
  return hourIn24;
};

export const TimePicker = ({ time, onTimeUpdate, disabled = false }: TimePickerProps) => {
  const isValidDate = isValid(time);

  // extract hour, minute, and am/pm from the time Date object
  const { hour, minute, amPm } = useMemo(() => {
    if (!isValidDate) return { hour: '', minute: '', amPm: '' };

    const hourIn24 = getHours(time);
    const minuteValue = getMinutes(time);

    return {
      hour: (hourIn24 % 12 || 12).toString(),
      minute: minuteValue.toString().padStart(2, '0'),
      amPm: hourIn24 >= 12 ? 'PM' : 'AM',
    };
  }, [time, isValidDate]);

  // construct a new Date object with the updated hour, minute, and am/pm
  const updateTime = (type: TimeComponent, value: string) => {
    if (!onTimeUpdate || !isValidDate) return;

    const currentHour = parseInt(hour, 10);
    const currentMinute = parseInt(minute, 10);
    const currentAmPm = amPm;

    const newHour = type === 'hour' ? parseInt(value, 10) : currentHour;
    const newMinute = type === 'minute' ? parseInt(value, 10) : currentMinute;
    const newAmPm = type === 'amPm' ? value : currentAmPm;

    const hourIn24 = convertTo24Hour(newHour, newAmPm);

    let newDate = setHours(time, hourIn24);
    newDate = setMinutes(newDate, newMinute);

    onTimeUpdate(newDate);
  };

  if (!isValidDate) {
    return (
      <Flex color="red.500" role="alert">
        Invalid time value
      </Flex>
    );
  }

  return (
    <Flex gap="8px" role="group">
      <Select value={hour} onValueChange={(value) => updateTime('hour', value)} disabled={disabled}>
        <SelectTrigger style={{ width: '40px' }}>
          <SelectValue placeholder="Hour" />
        </SelectTrigger>
        <SelectContent style={{ maxHeight: '400px', overflowY: 'auto' }}>
          {HOURS.map((h) => (
            <SelectItem key={h} value={h}>
              {h}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select value={minute} onValueChange={(value) => updateTime('minute', value)} disabled={disabled}>
        <SelectTrigger style={{ width: '40px' }}>
          <SelectValue placeholder="Minute" />
        </SelectTrigger>
        <SelectContent style={{ maxHeight: '400px', overflowY: 'auto' }}>
          {MINUTES.map((m) => (
            <SelectItem key={m} value={m}>
              {m}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select value={amPm} onValueChange={(value) => updateTime('amPm', value)} disabled={disabled}>
        <SelectTrigger style={{ width: '40px' }}>
          <SelectValue placeholder="AM/PM" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="AM">AM</SelectItem>
          <SelectItem value="PM">PM</SelectItem>
        </SelectContent>
      </Select>
    </Flex>
  );
};
